import { useState, useMemo } from "react";
import { useContracts } from "@/hooks/useContracts";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/useDebounce";
import { PaginatedBusinessIncome } from "@/components/v2/organisms/admin/tabs/BusinessIncomeTab/BusinessIncomeTable";

interface BusinessIncomeFilters {
  selectedOrganizations: string[];
  selectedProperties: string[];
  selectedIncomeType: string;
  startDate?: Date;
  endDate?: Date;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
}

export const useBusinessIncomeFilters = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [selectedIncomeType, setSelectedIncomeType] = useState<string>("all");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const { data: contracts = [] } = useContracts();
  const { data: allProperties = [] } = useProperties();
  const { data: allOrganizations = [] } = useOrganizations();

  // Compute filtered properties based on selected organizations (cascade logic)
  const filteredPropertiesForCascade = useMemo(() => {
    if (selectedOrganizations.length === 0) {
      return allProperties;
    }

    const propertiesInOrganizations = contracts
      .filter((contract) => 
        contract.status === "active" && 
        contract.organization_id &&
        selectedOrganizations.includes(contract.organization_id)
      )
      .map((contract) => contract.property_id);

    return allProperties.filter((property) =>
      propertiesInOrganizations.includes(property.id)
    );
  }, [selectedOrganizations, contracts, allProperties]);

  // Backend filters for RPC call
  const backendFilters = useMemo(() => {
    return {
      searchTerm: debouncedSearchTerm.trim() || undefined,
      propertyIds: selectedProperties.length > 0 ? selectedProperties : undefined,
      organizationIds: selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
      incomeType: selectedIncomeType !== "all" ? selectedIncomeType : undefined,
      dateFrom: startDate ? startDate.toISOString().split('T')[0] : undefined,
      dateTo: endDate ? endDate.toISOString().split('T')[0] : undefined,
      page: currentPage,
      pageSize,
    };
  }, [debouncedSearchTerm, selectedProperties, selectedOrganizations, selectedIncomeType, startDate, endDate, currentPage, pageSize]);

  // Fetch paginated data using RPC
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["business-income-paginated", backendFilters],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("search_business_income", {
        p_property_ids: backendFilters.propertyIds || null,
        p_organization_ids: backendFilters.organizationIds || null,
        p_income_type: backendFilters.incomeType || null,
        p_payment_type: null,
        p_date_from: backendFilters.dateFrom || null,
        p_date_to: backendFilters.dateTo || null,
        p_page: backendFilters.page,
        p_page_size: backendFilters.pageSize,
        p_order_by: "payment_date",
        p_order_direction: "desc",
        p_search_term: backendFilters.searchTerm || null,
      });

      if (error) throw error;
      return (data || []) as PaginatedBusinessIncome[];
    },
  });

  const paginatedIncomes = data || [];
  const totalCount = paginatedIncomes.length > 0 ? paginatedIncomes[0].total_count : 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const hasActiveFilters = useMemo(() => {
    return !!(
      searchTerm.trim() !== "" ||
      selectedOrganizations.length > 0 ||
      selectedProperties.length > 0 ||
      selectedIncomeType !== "all" ||
      startDate ||
      endDate
    );
  }, [searchTerm, selectedOrganizations, selectedProperties, selectedIncomeType, startDate, endDate]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedOrganizations([]);
    setSelectedProperties([]);
    setSelectedIncomeType("all");
    setStartDate(undefined);
    setEndDate(undefined);
    setCurrentPage(1);
  };

  // Pagination handlers
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const changePageSize = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Function to fetch all filtered incomes (for CSV export)
  const fetchAllFilteredIncomes = async (): Promise<PaginatedBusinessIncome[]> => {
    const { data, error } = await supabase.rpc("search_business_income", {
      p_property_ids: backendFilters.propertyIds || null,
      p_organization_ids: backendFilters.organizationIds || null,
      p_income_type: backendFilters.incomeType || null,
      p_payment_type: null,
      p_date_from: backendFilters.dateFrom || null,
      p_date_to: backendFilters.dateTo || null,
      p_page: 1,
      p_page_size: 10000, // Large number to get all records
      p_order_by: "payment_date",
      p_order_direction: "desc",
      p_search_term: backendFilters.searchTerm || null,
    });

    if (error) {
      console.error("Error fetching all filtered incomes:", error);
      throw error;
    }

    return (data || []) as PaginatedBusinessIncome[];
  };

  return {
    // Data
    paginatedIncomes,
    isLoading,
    error,
    refetch,

    // Pagination
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    goToPage,
    changePageSize,

    // Filter state
    searchTerm,
    setSearchTerm,
    selectedOrganizations,
    setSelectedOrganizations,
    selectedProperties,
    setSelectedProperties,
    selectedIncomeType,
    setSelectedIncomeType,
    startDate,
    setStartDate,
    endDate,
    setEndDate,

    // Computed
    allOrganizations,
    filteredPropertiesForCascade,
    hasActiveFilters,
    clearFilters,

    // Export function
    fetchAllFilteredIncomes,
  };
};